import React, { useMemo } from 'react';

import { dashboardStaffApis } from '@apis/next/staffApis';
import { DataGrid } from '@components/admin/DataGrid';
import { FormTitle, FormTitleAction } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Button } from '@components/Button';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { VerticalSpace } from '@components/VerticalSpace';
import { useSearch } from '@hooks/next/useSearch';
import { useAgencyList } from '@hooks/queries/dashboard/useAgencyList';
import { useUser } from '@hooks/queries/useUser';
import { StaffSearchQuery } from '@models/next/Staff';
import { Staff } from '@models/next/Staff';
import { mapRoleToRoleOptionLabel } from '@models/Role';
import { UserHelper } from '@models/User';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { DEFAULT_PAGE_SIZE, formatDateTime } from '@utils/types';
import { useHistory } from 'react-router-dom';
import { pick } from 'remeda';

const strings = createStrings({
  ja: {
    title: 'スタッフ一覧',
    addStaff: '新規追加',
    employeeId: '社員ID',
    name: '名前',
    email: 'メール',
    lastLogin: '前回ログインした時間',
    role: '権限グループ',
    tagSearchInputPlaceholder: '社員ID、名前やメールなどの検索条件を入力してください',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '1rem',
  },
  dataGrid: {
    margin: '1rem 0',
    width: '100%',
  },
  dataGridContainer: {
    flexShrink: 1,
    overflow: 'auto',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    marginTop: 36,
    alignItems: 'flex-end',
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
  },
  startContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  searchButton: {
    alignSelf: 'flex-start',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px) 30px repeat(2, 1fr 30px)',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
}));

const StaffsSearchForm: React.FC = () => {
  const { search, updateSearch } = useSearch(StaffSearchQuery);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(StaffSearchQuery, { initialOutput: search, enableReinitialize: true });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(pick(fields, ['staff_id', 'display_name', 'email']), form),
    [fields],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          shape="round"
          text={formStrings.filter}
          type="submit"
          size="small"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => {
            form.root.setOutput(empty);
          }}
        />
      </div>
    </form>
  );
};

export const Staffs: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();

  const actions: FormTitleAction[] = [
    {
      title: strings.addStaff,
      action: () => {
        history.push(url('/admin/staffs/AddStaff'));
      },
    },
  ];

  const { search, updateSearch } = useSearch(StaffSearchQuery);

  const staffsQuery = dashboardStaffApis.useGet('/dashboard/staff', {
    queries: {
      ...search,
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    },
  });

  const agencyListQuery = useAgencyList();

  const { data: userData } = useUser();

  const mapStaffsToRowItems = (staffs: Staff[]) =>
    staffs.map((staff) => ({
      ...staff,
      role: mapRoleToRoleOptionLabel(
        staff.role,
        agencyListQuery.data?.find((agency) => agency.id === staff.agency_id)?.name_en_short,
      ),
      real_name: new UserHelper(staff).fullname,
    }));

  return (
    <Layout title={strings.title}>
      <div className={classes.container}>
        <FormTitle title={strings.title} actions={actions} />
        <VerticalSpace height={20} />

        <StaffsSearchForm />

        <PageSizeForm />

        <QueryContainer queries={[agencyListQuery, staffsQuery]}>
          <div className={classes.dataGridContainer}>
            <DataGrid
              columns={[
                {
                  field: 'staff_id',
                  label: strings.employeeId,
                  grow: true,
                },
                {
                  field: 'real_name',
                  label: strings.name,
                  grow: true,
                },
                {
                  field: 'role',
                  label: strings.role,
                  menu: {
                    items: [
                      {
                        label: 'admin',
                        selected: search.role === 'admin',
                        onClick: () => updateSearch({ role: 'admin', agency_id: undefined }),
                      },
                      {
                        label: 'FC Japan',
                        selected: search.role === 'FCJapan',
                        onClick: () => updateSearch({ role: 'FCJapan', agency_id: undefined }),
                      },
                      ...(agencyListQuery.data?.map((agency) => ({
                        label: agency.name_en_short,
                        selected: search.role === 'FCChina' && search.agency_id === agency.id,
                        onClick: () => updateSearch({ role: 'FCChina', agency_id: agency.id }),
                      })) ?? []),
                    ],
                  },
                  grow: true,
                },
                {
                  field: 'email',
                  label: strings.email,
                  grow: true,
                },
                {
                  field: 'last_login',
                  label: strings.lastLogin,
                  grow: true,
                },
              ]}
              renderRow={{
                staff_id: (staff_id) => staff_id ?? '',
                real_name: (real_name) => real_name ?? '',
                role: (role) => role ?? '',
                email: (email) => email ?? '',
                last_login: (last_login) => (last_login ? formatDateTime(last_login) : ''),
              }}
              onClickRow={(row) => {
                if (userData?.id === row.id) {
                  history.push(url('/admin/staffs/Profile'));
                  return;
                }
                history.push(`${url('/admin/staffs/Staff')}?id=${row.id}`);
              }}
              rows={mapStaffsToRowItems(staffsQuery.data?.results ?? [])}
              classes={{
                container: classes.dataGrid,
              }}
            />
          </div>
        </QueryContainer>
        <div className={classes.paginationContainer}>
          <PaginationWithSearchQuery query={staffsQuery} />
        </div>
      </div>
    </Layout>
  );
};
